import { DefaultMap, defined } from "@glideapps/ts-necessities";
import { iterableEnumerate } from "collection-utils";
import type { ChangeObservable } from "common/change-obserable";
import { Data } from "common/components/data";
import type { AppEnvironment, AppOther, ComputationModel, DataStore, ValueProducer } from "common/components/types";
import type { TableName } from "common/database-strings";
import { nativeTableRowIDColumnName } from "common/database-strings";
import { logError } from "common/debug-print";
import type { TypeSchema } from "common/description";
import type { GeneratePublishedAppDataFromDataBody } from "common/firebase-function-types";
import { makeRowID } from "common/make-row-id";
import { callCloudFunctionWeb } from "sharedUI/lib/function-utils";
import { isArray, isResponseOK } from "support/index";
import type { Column, TableRelationData, Tables } from "./interface";

interface ColumnDataForValueProducer {
    [name: string]: unknown;
}

//store it directly here to reduce number of requests
const avatarList = [
    "/glide-brand-image/avatar/10.jpg",
    "/glide-brand-image/avatar/18.jpg",
    "/glide-brand-image/avatar/19.jpg",
    "/glide-brand-image/avatar/20.jpg",
    "/glide-brand-image/avatar/21.jpg",
    "/glide-brand-image/avatar/22.jpg",
    "/glide-brand-image/avatar/24.jpg",
    "/glide-brand-image/avatar/25.jpg",
    "/glide-brand-image/avatar/26.jpg",
    "/glide-brand-image/avatar/27.jpg",
    "/glide-brand-image/avatar/28.jpg",
    "/glide-brand-image/avatar/3.jpg",
    "/glide-brand-image/avatar/30.jpg",
    "/glide-brand-image/avatar/31.jpg",
    "/glide-brand-image/avatar/32.jpg",
    "/glide-brand-image/avatar/34.jpg",
    "/glide-brand-image/avatar/7.jpg",
    "/glide-brand-image/avatar/AlanEijk.jpg",
    "/glide-brand-image/avatar/BobbyDarret.jpg",
    "/glide-brand-image/avatar/ChristianaBright.jpg",
    "/glide-brand-image/avatar/ChristinaWallace.jpg",
    "/glide-brand-image/avatar/DerekJaston.jpg",
    "/glide-brand-image/avatar/ElinaSmith.jpg",
    "/glide-brand-image/avatar/EvanBrown.jpg",
    "/glide-brand-image/avatar/FoLamber.jpg",
    "/glide-brand-image/avatar/JanetJones.jpg",
    "/glide-brand-image/avatar/JoHampton.jpg",
    "/glide-brand-image/avatar/MarieRao.jpg",
    "/glide-brand-image/avatar/MelonieWillox.jpg",
    "/glide-brand-image/avatar/RyanHolbert.jpg",
    "/glide-brand-image/avatar/SarahSimon.jpg",
    "/glide-brand-image/avatar/TamsinWoodward.jpg",
];

const stopWords = new Set<string>(
    JSON.parse(
        '["a", "about", "above", "after", "again", "against", "ain", "all", "am", "an", "and", "any", "are", "aren", "aren\'t", "as", "at", "be", "because", "been", "before", "being", "below", "between", "both", "but", "by", "can", "couldn", "couldn\'t", "d", "did", "didn", "didn\'t", "do", "does", "doesn", "doesn\'t", "doing", "don", "don\'t", "down", "during", "each", "few", "for", "from", "further", "had", "hadn", "hadn\'t", "has", "hasn", "hasn\'t", "have", "haven", "haven\'t", "having", "he", "her", "here", "hers", "herself", "him", "himself", "his", "how", "i", "if", "in", "into", "is", "isn", "isn\'t", "it", "it\'s", "its", "itself", "just", "ll", "m", "ma", "me", "mightn", "mightn\'t", "more", "most", "mustn", "mustn\'t", "my", "myself", "needn", "needn\'t", "no", "nor", "not", "now", "o", "of", "off", "on", "once", "only", "or", "other", "our", "ours", "ourselves", "out", "over", "own", "re", "s", "same", "shan", "shan\'t", "she", "she\'s", "should", "should\'ve", "shouldn", "shouldn\'t", "so", "some", "such", "t", "than", "that", "that\'ll", "the", "their", "theirs", "them", "themselves", "then", "there", "these", "they", "this", "those", "through", "to", "too", "under", "until", "up", "ve", "very", "was", "wasn", "wasn\'t", "we", "were", "weren", "weren\'t", "what", "when", "where", "which", "while", "who", "whom", "why", "will", "with", "won", "won\'t", "wouldn", "wouldn\'t", "y", "you", "you\'d", "you\'ll", "you\'re", "you\'ve", "your", "yours", "yourself", "yourselves", "could", "he\'d", "he\'ll", "he\'s", "here\'s", "how\'s", "i\'d", "i\'ll", "i\'m", "i\'ve", "let\'s", "ought", "she\'d", "she\'ll", "that\'s", "there\'s", "they\'d", "they\'ll", "they\'re", "they\'ve", "we\'d", "we\'ll", "we\'re", "we\'ve", "what\'s", "when\'s", "where\'s", "who\'s", "why\'s", "would"]'
    )
);

interface withID {
    $rowID: ValueProducer;
}

function isWithID(obj: any): obj is withID {
    return "$rowID" in obj;
}

function isStringArray(obj: any): obj is string[] {
    if (Array.isArray(obj) === false) return false;
    for (const el of obj) {
        if (typeof el !== "string") return false;
    }
    return true;
}

export class LocalPlaygroundDataStore implements DataStore {
    private readonly tables: Map<string, ValueProducer>;
    //data as required by the backend to save it in Firestore
    private rawJSONRowObjectData: GeneratePublishedAppDataFromDataBody["tableData"];
    constructor(readonly schema: TypeSchema, tables: Tables, relColumns: TableRelationData) {
        this.schema = schema;
        this.tables = new Map();
        this.rawJSONRowObjectData = {};
        this.makeRequiredDataFormat(tables, relColumns);
    }

    public getRawJSONObjectData(): GeneratePublishedAppDataFromDataBody["tableData"] {
        return this.rawJSONRowObjectData;
    }

    //takes the data in the `Tables` interface format and creates arrays of object required to make value producers
    makeRequiredDataFormat(tables: Tables, relColumns: TableRelationData) {
        //takes an array representing a row as it's stored in tables and creates the {colName: colVal} representation needed
        const createRow = (data: unknown[], cols: Column[], i: number, avoidCols: Map<number, boolean>) => {
            const currRow: ColumnDataForValueProducer = {};
            cols.forEach((col, index) => {
                //only add column if it's not a relation
                if (avoidCols.get(index) === false || avoidCols.get(index) === undefined) {
                    //FIXME: fix this
                    const { name } = col;
                    currRow[name] = data[index];
                }
            });
            currRow["rowIndexColumnName"] = i;
            currRow["$rowID"] = makeRowID();
            return currRow;
        };
        //Initially fills data without relations
        //for every table, need to take its associated data and create the representation needed to create ValueProducers
        for (const [tableName, tableData] of Object.entries(tables.tables)) {
            const currentTableData: Array<ColumnDataForValueProducer> = [];
            //array of column indices which will not be added in createRow call
            const avoidRelColumns = new Map<number, boolean>();
            //get any relation columns for this table
            if (relColumns[tableName] !== undefined) {
                //get column index of the row
                Object.values(relColumns[tableName]).forEach(rel =>
                    avoidRelColumns.set(rel.columnInformation.colIndex, true)
                );
            }

            //iterate over rows (array is 2D)
            tableData.data.forEach((row, index) => {
                currentTableData.push(createRow(row, tableData.columns, index, avoidRelColumns));
            });
            this.rawJSONRowObjectData[tableName] = currentTableData;
            //make the value producer for the data without relations
            this.tables.set(tableName, Data.fromJSON<AppOther, AppEnvironment>(currentTableData));
        }
        //Go back and fils data with relations
        this.fillColumnsWithRelations(tables, relColumns);
        //Add images if applicable
        void this.addImageColumns(tables);
    }

    async addImageColumns(tables: Tables) {
        function getWordsInRow(row: Record<string, unknown>): ReadonlySet<string> {
            const wordRegex = /\p{L}+/gu;

            const words = new Set<string>();

            for (const [columnName, current] of Object.entries(row)) {
                if (columnName === nativeTableRowIDColumnName) continue;
                if (typeof current !== "string") continue;
                const str = current.toLocaleLowerCase();
                for (;;) {
                    const m = wordRegex.exec(str);
                    if (m === null) break;
                    const [s] = m;
                    if (s.length < 3) continue;
                    if (stopWords.has(s)) continue;
                    words.add(s);
                }
            }
            return words;
        }

        //gets the name column for a particular row
        const getNameColumn = (row: ValueProducer) => {
            if (row.current !== undefined && row.current !== null) {
                const data = row.getObjectInspector(undefined)?.getObjectValue();
                if (data !== undefined && data.name !== undefined) {
                    return data.name.current as string;
                }
            }
            return undefined;
        };

        const queriesAndSets: [string, (url: string) => void][] = [];

        for (const [tableName, tableData] of this.tables) {
            //check if it has an image first of all
            if (tables.tables[tableName].containsImage !== true) continue;

            //iterate through every row
            //TODO: add function which returns an iterator for a ValueProducer, since this code keeps coming up
            const rows = tableData.current;
            const jsonRows = this.rawJSONRowObjectData[tableName];
            if (!isArray(jsonRows) || !isArray(rows)) return;
            if (jsonRows.length !== rows.length) return;

            const wordsInRows = jsonRows.map(getWordsInRow);
            // The number of rows in which a word appears
            const wordCounts = new DefaultMap<string, number>(() => 0);
            for (const words of wordsInRows) {
                for (const w of words) {
                    wordCounts.update(w, c => c + 1);
                }
            }

            for (const [i, row] of iterableEnumerate(rows)) {
                //create the actual search query
                const words = defined(wordsInRows[i]);
                const searchQuery = getNameColumn(row) + " " + tableName.substring(13);
                const result = this.loadImageURI(searchQuery, i, tableName, row, words, wordCounts);
                if (result === undefined) continue;
                queriesAndSets.push(result);
            }
        }

        if (queriesAndSets.length === 0) return;

        //if we need to load unsplash images, we make one request to the backend to get all the images from unsplash (either from Firestore if they're saved
        //or load / call them) at once
        const results = await callCloudFunctionWeb("getAIPlaygroundImages", {
            imageQueries: queriesAndSets.map(([q]) => q),
        });
        if (!isResponseOK(results)) {
            logError("Error getting images from backend");
            return;
        }

        const data = await results?.json();
        if (isStringArray(data.results) && data.results.length === queriesAndSets.length) {
            for (let i = 0; i < data.results.length; i++) {
                const [, set] = queriesAndSets[i];
                set(data.results[i]);
            }
        } else {
            throw new Error("Non-string response recieved for images");
        }
    }

    loadImageURI(
        query: string,
        i: number,
        tableName: string,
        row: ValueProducer,
        words: ReadonlySet<string>,
        wordCounts: ReadonlyMap<string, number>
    ): [string, (url: string) => void] | undefined {
        const set = (image: string) => {
            this.rawJSONRowObjectData[tableName][i]["image"] = image;
            row.addProperty("image", new Data<AppOther, AppEnvironment>(image));
        };

        //non-exhaustive list
        if (
            query.includes("Relationship") ||
            query.includes("People") ||
            query.includes("Friend") ||
            query.includes("Family")
        ) {
            const randPerson = avatarList[Math.floor(Math.random() * avatarList.length)];
            set(`https://column.sh${randPerson}`);
            return undefined;
        } else {
            const uniqueWords = Array.from(words).filter(w => defined(wordCounts.get(w)) <= 1);
            if (uniqueWords.length > 0) {
                query = uniqueWords.join(" ");
            }
            return [query, set];
        }
    }

    fillColumnsWithRelations(tables: Tables, relColumns: TableRelationData) {
        const convertStringToArray = (value: string) => {
            //lists will be stored at strings like "{1, 2, 3}" so remove curly braces and split on commas
            return value.substring(1, value.length - 1).split(", ");
        };

        //helper method used to get the row object associated with a column value that has a relation
        //NOTE: pass in table that contains the data for the relation
        //FIXME: need to p
        const getRowObjectWithValue = (
            value: string,
            tableData: ValueProducer | undefined,
            rowRelArr: Array<string>
        ) => {
            if (tableData !== undefined) {
                if (tableData.current !== undefined && Array.isArray(tableData.current)) {
                    return tableData.current.find(element => {
                        //this is a row from the table we're searching for, now we need to check its columns
                        //FIXME: this is a little inefficient, clean up and make faster later, but table sizes we're operating on
                        //are very small so might not make a difference
                        const row = element.current;
                        if (row !== null && typeof row === "object") {
                            //iterate over the different columns and compare each value
                            for (const columnData of Object.values(row)) {
                                const colVal = columnData.current as string;
                                if (colVal === value) {
                                    if (isWithID(row)) {
                                        rowRelArr.push(row.$rowID.current as string);
                                    }
                                    return true;
                                }
                            }
                        }
                        return false;
                    });
                }
            }
            return undefined;
        };

        //calls function above for all of the related rows (since we have m to n relations i.e. a table may have a
        //list of other related objects)
        const getRowObjectsWithValue = (
            value: string[] | string,
            tableData: ValueProducer | undefined,
            rowRelArr: Array<string>
        ) => {
            if (Array.isArray(value)) {
                const data: ValueProducer[] = [];
                for (const val of value) {
                    const rowRes = getRowObjectWithValue(val, tableData, rowRelArr);
                    if (rowRes !== undefined) {
                        data.push(rowRes);
                    }
                }
                return new Data(data);
            } else {
                return getRowObjectWithValue(value, tableData, rowRelArr);
            }
        };

        for (const [tableName, relations] of Object.entries(relColumns)) {
            const rows = this.tables.get(tableName)?.current;
            if (Array.isArray(rows)) {
                rows.forEach((row, i) => {
                    //iterate over every relation for each row
                    relations.forEach(rel => {
                        const colName = rel.columnInformation.colName;
                        const colIndexInRow = rel.columnInformation.colIndex;
                        //get column value of relation column from initial tables
                        const colStringValue = tables.tables[tableName].data[i][colIndexInRow];
                        //FIXME: just putting the string value right now when sending it to the backend
                        delete this.rawJSONRowObjectData[tableName][i][colName];
                        //add column for comma separated list from which backend will derive list of relations
                        const commaSeparatedRowIDs: Array<string> = [];
                        //convert string list e.g. "{item1, item2, ...}"" if applicable into array of individual values
                        if (colStringValue !== undefined) {
                            const colValue =
                                colStringValue.startsWith("{") === true
                                    ? convertStringToArray(colStringValue)
                                    : colStringValue;
                            const dataForRelation = getRowObjectsWithValue(
                                colValue,
                                this.tables.get(rel.relatedToTable),
                                commaSeparatedRowIDs
                            );
                            this.rawJSONRowObjectData[tableName][i][`CS ${colName}`] = commaSeparatedRowIDs.join(",");
                            if (dataForRelation) {
                                //populate actual cell
                                row.addProperty(colName, dataForRelation);
                            }
                        }
                    });
                });
            }
        }
    }

    fetchTableRows(tableName: TableName): ValueProducer {
        const valProducer = this.tables.get(tableName.name);
        if (valProducer === undefined) {
            logError("Uh oh, table: ", tableName, " not found!");
            return Data.fromJSON<AppOther, AppEnvironment>({});
        }
        return valProducer;
    }

    fetchSingleLinkRow(link: ValueProducer): ValueProducer {
        return link;
    }

    fetchMultipleLinkRows(link: ValueProducer): ValueProducer {
        return link;
    }

    getComputationModelObservable(): ChangeObservable<ComputationModel | undefined> {
        throw new Error("panicing at getComputationModelObservable");
    }

    setAppEnvironment(): void {
        return;
    }

    appEnvironmentUpdated(): void {
        return;
    }

    //methods we panic at
    addRowToTable(): Promise<{ didAdd: boolean; row: ValueProducer | undefined }> {
        throw new Error("panicing at addRowToTable");
    }

    setColumnsInRow(): Promise<void> {
        throw new Error("panicing at setColumnsInRow");
    }
    deleteRowAtIndex(): Promise<void> {
        throw new Error("panicing at deleteRowAtIndex");
    }

    deleteRow(): Promise<void> {
        throw new Error("panicing at deleteRow");
    }

    clearTable(): Promise<void> {
        throw new Error("panicing at clearTable");
    }

    modifyComputedColumns(): void {
        throw new Error("panicing at modifyComputedColumns");
    }

    setEmailOwnersColumns(): void {
        throw new Error("panicing at setEmailOwnersColumns");
    }

    addComputedColumnsToData(): void {
        throw new Error("panicing at addComputedColumnsToData");
    }

    setSchema(): void {
        throw new Error("panicing at setSchema");
    }

    userProfileTableChanged(): void {
        throw new Error("panicing at userProfileTableChanged");
    }

    isRowOwnedByUser(): boolean {
        throw new Error("panicing at isRowOwnedByUser");
    }

    resetFromUpstream(): void {
        throw new Error("panicing at resetFromUpstream");
    }
}
